import { useCallback, useEffect, useState } from "react";
import { ThumbsDown, ThumbsUp } from "@phosphor-icons/react";
import { ShimmerText } from "~/components/assistant/ShimmerText";
import {
  NEGATIVE_IMPACT_LABELS,
  POSITIVE_IMPACT_LABELS,
} from "~/components/assistant/impact-options";
import { cn } from "~/lib/utils";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import type { ConversationAgentSummary } from "~/services/agents";
import {
  deleteTurnFeedback,
  putTurnFeedback,
  type FeedbackCreate,
  type TurnFeedback as TurnFeedbackValue,
} from "~/services/turns";
import { DeferredToolFallbackCard } from "./DeferredToolFallbackCard";
import { Markdown } from "./Markdown";
import { useMcpAppEntries } from "./McpAppEntriesContext";
import { McpAppFrame } from "./McpAppFrame";
import { ToolCallsDisclosure } from "./ToolCallsDisclosure";
import { useToolUIMap } from "./ToolUIContext";
import { TurnActionsMenu } from "./TurnActionsMenu";
import { TurnDiagnosticIndicator } from "./TurnDiagnosticIndicator";
import { useTurnDiagnostics } from "./TurnDiagnosticsContext";
import { TurnFeedback } from "./TurnFeedback";
import { HITL_TOOL_RENDERERS, isHitlTool } from "./hitlTools";
import { turnAnchorId } from "./useAnchoredScroll";
import type { AssistantTurnData, ToolCallEntry } from "./useTurns";

interface AssistantTurnProps {
  turn: AssistantTurnData;
  agent: ConversationAgentSummary | null;
  conversationId: string;
  // True between clicking Stop and the run ending. Only meaningful on the
  // in-flight turn; swaps the status label from "Thinking" to "Stopping".
  isCancelling?: boolean;
  // Admin-review mode: replace the thumbs bar with a static tinted feedback
  // card and an avatar thread line. No-op if the turn has no persisted feedback.
  readOnlyFeedback?: boolean;
  // Submit a response to a paused human-in-the-loop tool call, resuming the
  // run. Omitted in read-only contexts (e.g. admin review).
  onAnswer?: (toolCallId: string, answer: string) => void | Promise<void>;
  // Tool-call ids genuinely paused awaiting human input. Only these render an
  // answerable card; empty while a run is in flight or being reconciled.
  awaitingToolCallIds?: ReadonlySet<string>;
  // Reveal the hover copy-link-to-turn affordance. On for the read-only
  // shared/admin views; off for the live regular conversation.
  showTurnLinks?: boolean;
}

const EMPTY_AWAITING: ReadonlySet<string> = new Set();

export const AssistantTurn = ({
  turn,
  agent,
  conversationId,
  isCancelling,
  readOnlyFeedback = false,
  onAnswer,
  awaitingToolCallIds = EMPTY_AWAITING,
  showTurnLinks = false,
}: AssistantTurnProps) => {
  const showThinking = turn.isRunning;
  const hasText = turn.text.length > 0;
  const mcpAppEntries = useMcpAppEntries();
  const toolUIMap = useToolUIMap();
  const diagnostics = useTurnDiagnostics();

  // HITL tools with a bespoke card always render it; one without a card gets
  // the generic answer box while awaiting input, so a backend HITL tool can
  // ship ahead of its card. Everything else — including a non-HITL call left
  // un-resulted by an interrupted run — stays in the read-only pager.
  //
  // Placement keys off whether the call has a result, not `awaitingToolCallIds`:
  // an unanswered card renders below the response text (actionable prompt
  // nearest the input) and an answered one moves back into the tool flow above
  // it. `awaitingToolCallIds` empties during the post-run reconcile, so keying
  // placement on it would bounce the card above the text and back; the result
  // is stable across that window. Interactivity still gates on awaiting below.
  const isAwaiting = (tc: ToolCallEntry) => awaitingToolCallIds.has(tc.id);
  const isAnswered = (tc: ToolCallEntry) => tc.result !== undefined;
  const hasCard = (tc: ToolCallEntry) => tc.name in HITL_TOOL_RENDERERS;
  const isFallback = (tc: ToolCallEntry) => isHitlTool(tc.name) && !hasCard(tc) && isAwaiting(tc);
  const hitlCalls = turn.toolCalls.filter(hasCard);
  const answeredHitlCalls = hitlCalls.filter(isAnswered);
  const pendingHitlCalls = hitlCalls.filter((tc) => !isAnswered(tc));
  const fallbackCalls = turn.toolCalls.filter(isFallback);
  const otherCalls = turn.toolCalls.filter((tc) => !hasCard(tc) && !isFallback(tc));
  const hasTools = otherCalls.length > 0;
  const mcpFrames = turn.toolCalls.flatMap((tc) => {
    if (toolUIMap.has(tc.name)) return [];
    const entry = mcpAppEntries.get(tc.id);
    return entry?.html ? [{ id: tc.id, entry }] : [];
  });
  const showCancelled = turn.cancelled && !turn.isRunning;
  const showInteractiveFeedback =
    !readOnlyFeedback && !turn.isRunning && !turn.cancelled && !!turn.turnId;
  const adminFeedback = readOnlyFeedback ? binaryFeedback(turn) : null;
  // Deep-link anchor for this turn, normalized to the root trace id the user
  // message renders as `data-turn-id`. Present once the run has a turn id;
  // used for the copy-link permalink where `showTurnLinks` is enabled.
  const anchorId = turn.turnId ? turnAnchorId(turn.turnId) : undefined;
  // The diagnostics layer marks every identified turn, unscored ones included,
  // so the agent-label row has to appear even for an unnamed agent.
  const hasDiagnosticIndicator = !!diagnostics && !!anchorId;
  const isDiagnosticSelected = !!anchorId && diagnostics?.selectedTurnId === anchorId;
  // The hover "More actions" menu (message time + optional copy-link) shows on
  // every surface once the turn has a timestamp and has finished running.
  const showActionsMenu = !!turn.createdAt && !turn.isRunning;

  const [feedback, setFeedback] = useState<TurnFeedbackValue | FeedbackCreate | null>(
    turn.feedback ?? null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // True once we've seen this turn running this session. History turns mount
  // with isRunning=false, so `sawRunning` stays false and the feedback bar
  // appears without animation; live turns flip it true, so the bar animates in
  // when the run finishes.
  const [sawRunning, setSawRunning] = useState(false);
  useEffect(() => {
    if (turn.isRunning) setSawRunning(true);
  }, [turn.isRunning]);

  // Re-sync if /connect later returns a different feedback value for this
  // turn (e.g. the conversation reloaded after a navigation).
  useEffect(() => {
    setFeedback(turn.feedback ?? null);
  }, [turn.feedback]);

  const handleSubmit = useCallback(
    async (value: 0 | 1, text: string | null, impact: number | null) => {
      if (!turn.turnId || isSubmitting) return;
      setIsSubmitting(true);
      try {
        const payload: FeedbackCreate = {
          feedback_type: "BINARY",
          feedback_value: value,
          text,
          impact,
        };
        const result = await putTurnFeedback(conversationId, turn.turnId, payload);
        setFeedback(result);
      } catch (err) {
        console.error("Failed to submit turn feedback", err);
      } finally {
        setIsSubmitting(false);
      }
    },
    [conversationId, isSubmitting, turn.turnId]
  );

  const handleClear = useCallback(async () => {
    if (!turn.turnId || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await deleteTurnFeedback(conversationId, turn.turnId);
      setFeedback(null);
    } catch (err) {
      console.error("Failed to clear turn feedback", err);
    } finally {
      setIsSubmitting(false);
    }
  }, [conversationId, isSubmitting, turn.turnId]);

  // Shared by both render sites. onAnswer is passed only while awaiting, so a
  // cancelled turn can't resume a stopped run and read-only views stay inert.
  const renderHitlCard = (tc: ToolCallEntry) => {
    const Renderer = HITL_TOOL_RENDERERS[tc.name];
    return (
      <Renderer
        key={tc.id}
        entry={tc}
        isRunning={turn.isRunning}
        onAnswer={isAwaiting(tc) ? onAnswer : undefined}
        turnToolCalls={turn.toolCalls}
      />
    );
  };

  return (
    <li
      className={cn(
        "group/turn flex w-full items-stretch gap-3 self-start",
        // The tint is an underlay rather than padding on the row itself, so
        // selecting a turn leaves its content box untouched and the transcript
        // does not re-wrap on click. `isolate` keeps the negative z-index inside
        // this element, and the top bleed clears the agent-label row that sits
        // absolutely positioned 16px above the content.
        isDiagnosticSelected &&
          "relative isolate after:absolute after:-inset-x-2 after:-top-5 after:-bottom-1 after:-z-10 after:rounded-lg after:border after:border-border after:bg-muted/50 after:content-['']"
      )}
    >
      <div className="flex w-7 flex-col items-center">
        <AgentAvatar agent={agent} size={28} aria-hidden="true" />
        {adminFeedback && (
          <div
            className={cn(
              "mt-1 w-0.5 flex-1 rounded-full",
              adminFeedback.tone === "positive" ? "bg-emerald-500/60" : "bg-destructive/60"
            )}
          />
        )}
      </div>
      <div className="relative flex min-w-0 flex-1 flex-col gap-2">
        {(agent?.display_name || showThinking || hasDiagnosticIndicator) && (
          <div className="absolute -top-4 left-1 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
            {agent?.display_name && <span>{agent.display_name}</span>}
            {showThinking && (
              <span className="inline-flex items-center gap-1.5" aria-live="polite">
                {agent?.display_name && <span aria-hidden>·</span>}
                <ShimmerText>{isCancelling ? "Stopping" : "Thinking"}</ShimmerText>
              </span>
            )}
            <TurnDiagnosticIndicator turnId={anchorId} />
          </div>
        )}
        {hasTools && (
          // `readOnlyFeedback` marks the shared-link and admin views — the same
          // two places where the reader does not own the conversation.
          <ToolCallsDisclosure
            entries={otherCalls}
            isRunning={turn.isRunning}
            readOnlyView={readOnlyFeedback}
          />
        )}
        {turn.toolCalls.map(({ id, name, args, result }) => {
          const UI = toolUIMap.get(name);
          if (!UI) return null;
          return (
            <UI key={id} toolCallId={id} args={args} result={result} isRunning={turn.isRunning} />
          );
        })}
        {answeredHitlCalls.map(renderHitlCard)}
        {hasText && (
          <div className="px-1 py-1 text-sm text-foreground">
            <Markdown text={turn.text} />
          </div>
        )}
        {pendingHitlCalls.map(renderHitlCard)}
        {fallbackCalls.map((tc) => (
          <DeferredToolFallbackCard key={tc.id} entry={tc} onAnswer={onAnswer} />
        ))}
        {mcpFrames.map(({ id, entry }) => (
          <McpAppFrame
            key={id}
            html={entry.html!}
            title={entry.title}
            toolResult={entry.toolResult}
          />
        ))}
        {showCancelled && (
          <div className="px-1 text-xs italic text-muted-foreground" aria-live="polite">
            Response cancelled
          </div>
        )}
        {(showInteractiveFeedback || showActionsMenu) && (
          <div
            className={cn(
              "-ml-1 mt-0.5 flex items-center gap-1",
              showInteractiveFeedback &&
                sawRunning &&
                "animate-in fade-in slide-in-from-bottom-1 fill-mode-both duration-300 delay-150 ease-out"
            )}
          >
            {showInteractiveFeedback && (
              <TurnFeedback
                feedback={feedback}
                isSubmitting={isSubmitting}
                onSubmit={handleSubmit}
                onClear={handleClear}
              />
            )}
            {showActionsMenu && turn.createdAt && (
              <TurnActionsMenu
                createdAt={turn.createdAt}
                outputText={turn.text}
                turnId={anchorId}
                showCopyLink={showTurnLinks}
              />
            )}
          </div>
        )}
        {adminFeedback && <FeedbackCard feedback={adminFeedback} />}
      </div>
    </li>
  );
};

interface BinaryFeedback {
  tone: "positive" | "negative";
  impactLabel: string | null;
  text: string | null;
}

function binaryFeedback(turn: AssistantTurnData): BinaryFeedback | null {
  const fb = turn.feedback;
  if (!fb || fb.feedback_type !== "BINARY" || fb.feedback_value == null) return null;
  const isPositive = fb.feedback_value === 1;
  return {
    tone: isPositive ? "positive" : "negative",
    impactLabel: impactLabel(fb, isPositive),
    text: fb.text?.trim() || null,
  };
}

function impactLabel(fb: TurnFeedbackValue, isPositive: boolean): string | null {
  if (fb.impact == null) return null;
  const table = isPositive ? POSITIVE_IMPACT_LABELS : NEGATIVE_IMPACT_LABELS;
  return table[fb.impact] ?? null;
}

const FeedbackCard = ({ feedback }: { feedback: BinaryFeedback }) => {
  const isPositive = feedback.tone === "positive";
  return (
    <div
      className={cn(
        "flex flex-col gap-1 rounded-md border px-3 py-2 text-xs",
        isPositive
          ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-800"
          : "border-destructive/30 bg-destructive/[0.06] text-destructive"
      )}
    >
      <div className="flex items-center gap-1.5">
        {isPositive ? <ThumbsUp size={14} weight="fill" /> : <ThumbsDown size={14} weight="fill" />}
        <span className="font-medium">User feedback</span>
        {feedback.impactLabel && (
          <span className="font-normal opacity-80">· {feedback.impactLabel}</span>
        )}
      </div>
      {feedback.text && <div className="italic">&ldquo;{feedback.text}&rdquo;</div>}
    </div>
  );
};
